import Separator from "@/v2-ui/separator"
import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import FeatureConfigurationEditor from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationEditor"
import FeatureConfigurationElsewhere from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationElsewhere"
import FeatureConfigurationSkeleton from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationSkeleton"
import FeatureUnpublishedNotice from "@/v2-backoffice-client/feature/FeatureUnpublishedNotice"
import { CONFIGURATION_OWNER_BY_FEATURE_TYPE } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"
import { getFeatureEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.model"
import FeatureTypeSection from "@/v2-backoffice-client/feature/type/FeatureTypeSection"
import { useFeatureQuery } from "@/v2-backoffice-client/feature/feature.query"
import { useFeatureUpdateMutation } from "@/v2-backoffice-client/feature/update/feature.update.mutation"

export type FeatureConfigurationTabProps = {
  className?: string
  featureCode: string
  /** Surface segment of this tab's feature codes, e.g. "Features". */
  featureCodeLabel: string
}

/** An empty object, not an empty file: a feature with no configuration yet
 * should start from something the editor can parse and the operator can add
 * a key to. */
const EMPTY_CONFIGURATION = "{}"

/**
 * What a feature is and how it behaves: its kind, then the configuration that
 * kind implies — 3D building style presets, search providers, whatever type
 * comes next.
 *
 * The two belong on one tab because the first decides the second. Basemaps and
 * map layers land here too (every one of them is a feature), but their
 * Configuration is not on the feature row, so this sends the operator to the
 * catalog that does own it rather than offering an editor over a column nothing
 * reads — and the kind that put them there is locked for the same reason.
 */
function FeatureConfigurationTab(props: FeatureConfigurationTabProps) {
  const { featureCode, featureCodeLabel, className } = props

  const { data, loading } = useFeatureQuery({ variables: { featureCode } })
  const feature = data?.getFeature

  const [save, { loading: isSaving, error }] = useFeatureUpdateMutation({
    onCompleted() {
      toast({ title: "Configuration saved to the live runtime." })
    },
  })
  errorToast(error, { title: "Failed to save configuration" })

  if (loading) return <FeatureConfigurationSkeleton className={className} />

  if (!feature) {
    return (
      <div className={cn("p-3", className)}>
        <FeatureUnpublishedNotice subject="feature" />
      </div>
    )
  }

  const owner = CONFIGURATION_OWNER_BY_FEATURE_TYPE[feature.FeatureTypeCode]

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <section className="flex flex-col gap-2 px-3 pt-3">
        <FeatureTypeSection
          feature={feature}
          featureCodeLabel={featureCodeLabel}
        />
      </section>

      <Separator />

      {owner ? (
        <FeatureConfigurationElsewhere
          className="mx-3 mb-3"
          featureCode={feature.Code}
          owner={owner}
        />
      ) : (
        <FeatureConfigurationEditor
          modelPath={getFeatureEditorModelPath()}
          value={
            feature.Configuration
              ? JSON.stringify(feature.Configuration, null, 2)
              : EMPTY_CONFIGURATION
          }
          isSaving={isSaving}
          onSave={(configuration) => {
            void save({
              variables: {
                input: {
                  FeatureCode: feature.Code,
                  Configuration: configuration,
                },
              },
            })
          }}
        />
      )}
    </div>
  )
}

export default FeatureConfigurationTab
